import React, { useEffect, useState } from 'react'
import {
	Button,
	Drawer,
	message,
	Popconfirm,
	Select,
	Space,
	Table,
	TablePaginationConfig,
	Tag,
	Tooltip,
	Typography,
} from 'antd'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getInvoicesAction, getSingleInvoicesAction } from 'slices/invoice'
import moment from 'moment'
import {
	DeleteOutlined,
	EyeOutlined,
	WalletOutlined,
	EditOutlined,
} from '@ant-design/icons'
import { ClientProfile, Invoice, Plan } from 'types'
import SingleInvoiceTable from 'component/Invoices/SingleInvoiceTable'
import NewInvoiceForm from 'component/NewInvoiceForm'
import deleteInvoice from 'api/invoice/deleteInvoice'
import AcceptPaymentForm from 'component/Payments/AcceptPaymentForm'
import UpdateInvoiceForm from 'component/Invoices/UpdateInvoiceForm'
import addCurrencySymbol from 'utils/addCurrencySymbol'

const { Title } = Typography
const { Option } = Select

/**
 * Hardcoded size of the table
 */
const size = 10

interface PropTypes {
	clientId: string
}

const InvoiceTable: React.FC<PropTypes> = ({ clientId }) => {
	const [drawer, setDrawer] = useState(false)
	const [invoiceFormDrawer, setInvoiceFormDrawer] = useState(false)
	const [paymentFormDrawer, setPaymentFormDrawer] = useState(false)
	const [invoiceId, setinvoiceId] = useState('')
	const [updateInvoiceRef, setUpdateInvoiceRef] = useState<Invoice | null>(null)
	const [isPaid, setIsPaid] = useState<boolean | undefined>(undefined)
	const [isDeleteInvoice, setIsDeleteInvoice] = useState(false)
	const dispatch = useAppDispatch()
	const { records, currentPage, totalCount, loading, invoice } = useAppSelector(
		state => state.invoice
	)

	const currency =
		useAppSelector(
			state => state.singleClient.clientProfile?.currency_selected
		) ?? 'INR'

	/**
	 * Pagination config for the table
	 */
	const pagination = {
		current: currentPage,
		total: totalCount,
		pageSize: size,
	}

	/**
	 * Fetch Client Invoice on mount
	 */

	useEffect(() => {
		const options = {
			page: pagination.current,
			size: pagination.pageSize,
			isPaid,
			client: clientId,
		}
		dispatch(getInvoicesAction(options))
	}, [dispatch, currentPage, clientId, isPaid])

	/**
	 * Handles pagination change by changing the current page in the store.
	 *
	 * @param page - Pagination object from the table
	 */

	const handlePageChange = (page: TablePaginationConfig) => {
		const options = {
			page: page.current || 1,
			size: page.pageSize || size,
			isPaid,
			client: clientId,
		}
		dispatch(getInvoicesAction(options))
	}

	/**
	 * Open Drawer for Single invoice view
	 */
	const openDrawer = (id: string) => {
		setDrawer(true)
		dispatch(getSingleInvoicesAction(id))
	}

	/**
	 * Close Drawer for Single invoice view
	 */
	const closeDrawer = () => {
		setDrawer(false)
	}

	/**
	 * Open Drawer for New Invoice Form
	 */
	const openInvoiceFormDrawer = () => {
		setInvoiceFormDrawer(true)
	}

	/**
	 * Close Drawer for New Invoice Form
	 */
	const closeInvoiceFormDrawer = () => {
		setInvoiceFormDrawer(false)
	}

	/**
	 * Open Drawer for Accept Payment Form
	 */
	const openPaymentFormDrawer = (id: string) => {
		setPaymentFormDrawer(true)
		setinvoiceId(id)
	}

	/**
	 * Close Drawer for Accept Payment Form
	 */
	const closePaymentFormDrawer = () => {
		setPaymentFormDrawer(false)
	}

	/**
	 * Open Drawer for Update Invoice Form
	 */
	const openUpdateInvoiceForm = (invoiceRef: Invoice) => {
		setUpdateInvoiceRef(invoiceRef)
	}

	/**
	 * Close Drawer for Update Invoice Form
	 */
	const closeUpdateInvoiceForm = () => {
		setUpdateInvoiceRef(null)
	}

	/**
	 * Function for filter Paid & Unpaid
	 * @param value :Selected option value for filter
	 */
	const handleFilterChange = (value: string) => {
		if (value === 'yes') {
			setIsPaid(true)
		} else if (value === 'no') {
			setIsPaid(false)
		} else {
			setIsPaid(undefined)
		}
	}

	/**
	 * Call on success of New Invoice form submit
	 * @param val : val will be true on form success
	 */
	const onSuccessNewInvoice = (val: boolean) => {
		if (val) {
			setInvoiceFormDrawer(false)
			setPaymentFormDrawer(false)
			const options = {
				page: 1,
				size: pagination.pageSize,
				isPaid,
				client: clientId,
			}
			dispatch(getInvoicesAction(options))
		}
	}

	/**
	 * Call on success of Update Invoice form submit
	 */
	const onSuccessUpdateInvoice = () => {
		closeUpdateInvoiceForm()

		const options = {
			page: 1,
			size: pagination.pageSize,
			isPaid,
			client: clientId,
		}

		dispatch(getInvoicesAction(options))
	}

	const handleDeleteConfirm = async (id: string) => {
		try {
			setIsDeleteInvoice(true)
			await deleteInvoice({ id })
			setIsDeleteInvoice(false)
			message.success('Invoice deleted!')
			const options = {
				page: pagination.current,
				size: pagination.pageSize,
				isPaid,
				client: clientId,
			}
			dispatch(getInvoicesAction(options))
		} catch (error) {
			setIsDeleteInvoice(false)
		}
	}

	/**
	 * Columns for the table of Invoice
	 */

	const columns = [
		{
			title: 'Number',
			dataIndex: 'number',
		},
		{
			title: `Amount (${currency})`,
			dataIndex: 'amount',
			render: (amount: number, record: Invoice) =>
				addCurrencySymbol(
					amount + (record.tdsAmount ?? 0),
					record.currency ?? currency
				),
		},
		{
			title: `Receiveable (${currency})`,
			dataIndex: 'amount',
			render: (amount: number, record: Invoice) =>
				addCurrencySymbol(amount, record.currency ?? currency),
		},
		{
			title: `TDS (${currency})`,
			dataIndex: 'tdsAmount',
			render: (tdsAmount: number | undefined, record: Invoice) =>
				record.tdsEnabled
					? addCurrencySymbol(tdsAmount ?? 0, record.currency ?? currency)
					: '-',
		},
		{
			title: 'Date',
			dataIndex: 'createdAt',
			render: (createdAt: string) => moment(createdAt).format('DD/MM/YYYY'),
		},
		{
			title: 'Due Date',
			dataIndex: 'dueDate',
			render: (dueDate: string | null) =>
				dueDate ? moment(dueDate).format('DD/MM/YYYY') : 'NA',
		},
		{
			title: 'Will Deactivate',
			dataIndex: 'shouldDeactivateAccount',
			render: (deactivation: boolean | undefined) =>
				deactivation ? <Tag color='red'>Yes</Tag> : <Tag color='green'>No</Tag>,
		},
		{
			title: 'Client Name',
			dataIndex: 'client',
			render: (client: ClientProfile) => (client ? client.organizationName : '--'),
		},
		{
			title: 'Client Email',
			dataIndex: 'client',
			render: (client: ClientProfile) => (client ? client.email : '--'),
		},
		{
			title: 'Paid',
			dataIndex: 'isPaid',
			render: (item: boolean) =>
				item ? <Tag color='green'>Yes</Tag> : <Tag color='red'>No</Tag>,
		},
		{
			title: `Paid Amount (${currency})`,
			dataIndex: 'paidAmount',
			render: (paidAmount: number, record: Invoice) =>
				addCurrencySymbol(paidAmount, record.currency ?? currency),
		},
		{
			title: 'Selected Plan',
			dataIndex: 'selectedPlan',
			render: (selectedPlan: Plan) => selectedPlan.billingCycle,
		},
		{
			title: 'Plan Price',
			dataIndex: 'selectedPlan',
			render: (selectedPlan: Plan, record: Invoice) =>
				selectedPlan.billingCycle === 'MONTHLY'
					? addCurrencySymbol(selectedPlan.perMonth, record.currency ?? currency)
					: addCurrencySymbol(selectedPlan.perYear, record.currency ?? currency),
		},
		{
			title: 'Deleted',
			dataIndex: 'isDeleted',
			render: (item: boolean) =>
				item ? <Tag color='red'>Yes</Tag> : <Tag color='green'>No</Tag>,
		},
		{
			title: 'Action',
			dataIndex: '_id',
			key: 'action',
			width: 180,
			fixed: 'right' as 'right',
			render: (_id: string, row: Invoice) => (
				<Space>
					<Tooltip placement='bottomLeft' title='View'>
						<Button
							type='primary'
							icon={<EyeOutlined />}
							onClick={() => openDrawer(_id)}
						/>
					</Tooltip>
					<Tooltip placement='bottomLeft' title='Delete'>
						{row.isDeleted || row.paidAmount !== 0 ? (
							<Button type='primary' icon={<DeleteOutlined />} disabled />
						) : (
							<Popconfirm
								title='Are you sure to delete this invoice?'
								placement='left'
								onConfirm={() => handleDeleteConfirm(_id)}
								okText='Delete'
								okButtonProps={{ loading: isDeleteInvoice }}
								cancelText='Cancel'
							>
								<Button type='primary' icon={<DeleteOutlined />} />
							</Popconfirm>
						)}
					</Tooltip>
					<Tooltip placement='bottomLeft' title='Payment'>
						<Button
							type='primary'
							icon={<WalletOutlined />}
							onClick={() => openPaymentFormDrawer(_id)}
							disabled={row.isPaid === true || row.isDeleted === true}
						/>
					</Tooltip>
					<Tooltip placement='bottomRight' title='Update'>
						<Button
							type='primary'
							icon={<EditOutlined />}
							onClick={() => openUpdateInvoiceForm(row)}
							// disabled={row.isPaid === true || row.isDeleted === true}
							disabled={row.isDeleted === true}
						/>
					</Tooltip>
				</Space>
			),
		},
	]

	return (
		<>
			<div
				className='head-align-flex'
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					paddingBottom: '1rem',
					marginTop: '1rem',
				}}
			>
				<div className='Title-Head'>
					<Title level={5}>Invoice</Title>
				</div>
				<div>
					<Select
						onChange={handleFilterChange}
						placeholder='Filter'
						style={{ width: 115, marginLeft: '10px' }}
					>
						<Option value='all'>All</Option>
						<Option value='yes'>Paid</Option>
						<Option value='no'>Unpaid</Option>
					</Select>
					<span style={{ marginLeft: '10px' }}>
						<Tooltip placement='bottomLeft' title='Create New Invoice'>
							<Button type='primary' onClick={() => openInvoiceFormDrawer()}>
								New Invoice
							</Button>
						</Tooltip>
					</span>
				</div>
			</div>
			<Table
				columns={columns}
				rowKey={record => record._id}
				dataSource={records}
				pagination={{ ...pagination, position: ['bottomCenter'] }}
				loading={loading}
				onChange={handlePageChange}
				scroll={{ x: 'calc(100vw + 0.35rem)' }}
			/>
			<Drawer
				title={`Invoice Number: ${invoice?.number}`}
				placement='right'
				width='38vw'
				closable
				visible={drawer}
				onClose={closeDrawer}
				destroyOnClose
			>
				<SingleInvoiceTable invoiceData={invoice} loading={loading} />
			</Drawer>
			<Drawer
				title='Create New Invoice'
				placement='right'
				width='33vw'
				closable
				visible={invoiceFormDrawer}
				onClose={closeInvoiceFormDrawer}
				destroyOnClose
			>
				<NewInvoiceForm clientId={clientId} onSuccess={onSuccessNewInvoice} />
			</Drawer>
			<Drawer
				title='Accept Payment'
				placement='right'
				width='33vw'
				closable
				visible={paymentFormDrawer}
				onClose={closePaymentFormDrawer}
				destroyOnClose
			>
				<AcceptPaymentForm invoiceId={invoiceId} onSuccess={onSuccessNewInvoice} />
			</Drawer>
			<Drawer
				title='Update Invoice'
				placement='right'
				width='33vw'
				closable
				visible={!!updateInvoiceRef}
				onClose={closeUpdateInvoiceForm}
				destroyOnClose
			>
				{updateInvoiceRef !== null && (
					<UpdateInvoiceForm
						invoice={updateInvoiceRef}
						onSuccess={onSuccessUpdateInvoice}
					/>
				)}
			</Drawer>
		</>
	)
}

export default InvoiceTable
